import React, { useCallback, useEffect } from 'react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Box, InputBase, makeStyles, Typography, withStyles } from '@material-ui/core';
import _ from 'lodash';
import { DropDownIcon } from '../Icon';
import { classNames } from '../../utils';
import { Checkbox } from '../../page/DutyAssign/components/Attendance/Setting';
import { i18n } from '../../language';

const useStyles = makeStyles((theme) => ({
  inputBox: (props) => ({
    position: 'relative',
    height: props.height ? props.height : '3.5rem',
    width: props.width ? props.width : '100%',
    border: 'solid 1px #e4e7eb',
    borderRadius: '0.625rem',
    background: '#fff',
  }),
  inputBase: (props) => ({
    height: '100%',
    width: '100%',
    lineHeight: props.height ?? '3.5rem',
    borderRadius: '0.625remm',
    paddingLeft: '1.25rem',
    paddingRight: '2rem',
    cursor: props.onlySelect ? 'pointer' : 'unset',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
      height: 'unset',
      cursor: props.onlySelect ? 'pointer' : 'unset',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // whiteSpace: 'nowrap',
      // width: '9rem',
    },
  }),
  dropDownIcon: {
    position: 'absolute',
    top: '50%',
    right: '1rem',
    transform: 'translateY(-50%)',
    height: '0.875rem',
    width: '0.875rem',
    color: '#538fff',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  error: {
    borderColor: '#f44336 !important',
  },
  errorMsg: ({ errMsgMargin, errMsgWidth }) => ({
    margin: errMsgMargin,
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    width: errMsgWidth,
  }),
  disabledCheckbox: {
    background: '#c0c6cb !important',
  },
  highlight: (props) => ({
    backgroundColor: props.highlight,
  }),
}));

function AutoCompleteMultiCheckbox({
  id,
  classes,
  itemList,
  className,
  placeholder,
  value,
  onChange,
  width,
  height,
  style,
  onOpen,
  onBlur,
  errorMsg,
  error,
  dropDownIcon,
  onlySelect = false,
  align = 'left',
  allText = 'All',
  errMsgMargin = '3px 0.825rem 0 0.825rem',
  errMsgWidth = 'auto',
  showAllText = false,
  selectAll = true,
  fixedData = [],
  reset = 1,
  freeSolo = false,
  highlight,
}) {
  const styles = useStyles({ width, height, errMsgMargin, errMsgWidth, onlySelect, highlight });
  const [inputValue, setInputValue] = React.useState('');
  const allSelected = value.length === itemList.length;
  const filter = createFilterOptions();
  const [isFocus, setIsFocus] = React.useState(false);

  const handleFreeSolo = () => {
    if (freeSolo && inputValue) {
      const newValue = _.uniqBy(
        [
          ...value,
          {
            text: inputValue,
            value: inputValue,
          },
        ],
        'value'
      );
      onChange(newValue);
      setInputValue('');
    }
  };

  // reset by outside
  useEffect(() => {
    setInputValue('');
  }, [reset]);

  // select all 事件
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onChange([...fixedData]);
    } else {
      onChange(itemList);
    }
  }, [allSelected, itemList, onChange, fixedData]);

  const initInputValue = () => {
    if (showAllText) {
      setInputValue(_.map(value, (item) => item.text).join(','));
    } else if (value?.length === 1) {
      setInputValue(value[0].text);
    } else if (selectAll && (allSelected || value?.length === 0)) {
      setInputValue(allText);
    } else if (value?.length > 1) {
      setInputValue(
        i18n.t('DUTYASSIGN_TEMPLATE_AUTO_RULE_SELECTED', {
          value: value.length,
        })
      );
    } else {
      setInputValue('');
    }
  };

  useEffect(() => {
    if (!isFocus) {
      initInputValue();
    }
  }, [value, allText]);

  const getErroeClass = (props) => {
    return error || errorMsg ? classNames(props, styles.error) : props;
  };

  // 輸入框input
  const inputBody = (params) => {
    const dropDownHandle = () => {
      params.inputProps.ref.current.childNodes[0].focus();
      params.inputProps.onMouseDown();
    };

    return (
      <div ref={params.InputProps.ref} className={getErroeClass(styles.inputBox)}>
        <InputBase
          {...params.inputProps}
          placeholder={placeholder}
          className={classNames(styles.inputBase, value?.length > 0 && highlight ? styles.highlight : '')}
          readOnly={onlySelect}
          onMouseDown={params.inputProps.onMouseDown}
          type="text"
          onBlur={(e) => {
            params.inputProps.onBlur(e);
            if (typeof onBlur === 'function') {
              onBlur();
            }
          }}
          onKeyDown={(event) => {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
              handleFreeSolo();
            }
          }}
        />
        {dropDownIcon && <DropDownIcon className={styles.dropDownIcon} onClick={dropDownHandle} />}
      </div>
    );
  };

  // 渲染選項
  const renderOptionCtr = (option, { selected }) => {
    const selectAllProps = option.value === 'select-all' ? { check: allSelected } : {};
    const fixed = _.some(fixedData, (item) => item.value === option.value);
    const fixStyle = fixed ? { className: styles.disabledCheckbox } : {};

    return align === 'left' ? (
      <>
        <Checkbox check={selected} {...selectAllProps} />
        {option.text}
      </>
    ) : (
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100vw" p="0.5rem 0">
        <Box>{option.text}</Box>
        <Checkbox check={selected} {...selectAllProps} {...fixStyle} />
      </Box>
    );
  };

  // 組件本體onBlur事件
  const handleOnBlur = () => {
    handleFreeSolo();
    setIsFocus(false);
    initInputValue();
  };

  // 組件本體onFocus事件
  const handleOnFocus = () => {
    setIsFocus(true);
    setInputValue('');
  };

  // 選擇item事件
  const handleOnChange = (event, newValue, reason) => {
    if (reason === 'select-option' || reason === 'remove-option') {
      if (newValue.some((option) => option.value === 'select-all')) {
        handleSelectAll();
        freeSolo && setInputValue('');
      } else if (fixedData.length > 0 && typeof onChange === 'function') {
        let tempNewValue = _.filter(
          newValue,
          (item) => !_.some(fixedData, (fixedItem) => fixedItem.value === item.value)
        );
        onChange([...tempNewValue, ...fixedData]);
        freeSolo && setInputValue('');
      } else if (typeof onChange === 'function') {
        onChange(newValue);
        freeSolo && setInputValue('');
      }
    }
  };

  // input框事件
  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') {
      return;
    }
    setInputValue(newInputValue);
  };

  // 獲取list item的文本
  const hanldeGetOptionLabel = (option) => option.text;

  // 獲取list item的選擇狀態
  const hanldeGetOptionSelected = (option, anotherOption) => option.value === anotherOption.value;

  // 處理過濾list item
  const hanldeFilterOptions = (options, params) => {
    const filtered = filter(options, params);
    return [{ text: allText, value: 'select-all' }, ...filtered].slice(selectAll ? 0 : 1);
  };

  return (
    <Box display="flex" style={style} flexDirection="column" onBlur={handleOnBlur} onFocus={handleOnFocus}>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        classes={classes}
        className={classNames(styles.root, className)}
        value={value}
        onChange={handleOnChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={Array.isArray(itemList) ? itemList : []}
        getOptionLabel={hanldeGetOptionLabel}
        renderInput={inputBody}
        style={style}
        renderOption={renderOptionCtr}
        onOpen={onOpen}
        getOptionSelected={hanldeGetOptionSelected}
        filterOptions={hanldeFilterOptions}
      />
      {errorMsg ? <Typography className={styles.errorMsg}>{errorMsg}</Typography> : ''}
    </Box>
  );
}

export default withStyles({
  paper: {
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
  },
  listbox: {
    '& li[aria-selected="true"]': {
      color: 'unset',
      background: 'unset',
      cursor: 'pointer',
    },
    '& li[data-focus="true"]': {
      color: '#538fff',
      background: '#f5faff',
      cursor: 'pointer',
    },
  },
  option: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
    paddingRight: 0,
    wordBreak: 'break-word',
    '&>div:first-child': {
      marginRight: '0.75rem',
    },
  },
})(AutoCompleteMultiCheckbox);
